import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandContext = createContext();

export const BRAND_CONFIG = {
  blackswan: {
    name: "Trend Sentinel",
    fullName: "Black Swan_Trend Sentinel",
    industry: "Corporate Services",
    tagline: "Competitive Intelligence Engine",
    logo: "/blackswan-logo.png",
    theme: {
      primary: "#DC143C",       // Crimson
      secondary: "#FFD700",     // Gold
      background: "#09090b",    // Dark
      surface: "#161b22",
      surfaceHover: "#21262d",
      border: "#30363d",
      text: "#ffffff",
      textMuted: "#8b949e",
    },
    isDark: true,
  },
  blackswan_realestate: {
    name: "Trend Sentinel RE",
    fullName: "Black Swan_Trend Sentinel Real Estate",
    industry: "Real Estate",
    tagline: "Real Estate Intelligence Hub",
    logo: "/blackswan-realestate-logo.png",
    theme: {
      primary: "#D4AF37",       // Gold
      secondary: "#DC143C",     // Crimson
      background: "#0a0a0a",    // Dark
      surface: "#141414",
      surfaceHover: "#1f1f1f",
      border: "#2a2a2a",
      text: "#ffffff",
      textMuted: "#a0a0a0",
    },
    isDark: true,
  },
  prazi: {
    name: "Prazi",
    fullName: "Prazi Medical",
    industry: "Surgical Equipment",
    tagline: "Medical Intelligence Hub",
    logo: "/prazi-logo.png",
    theme: {
      primary: "#059669",       // Emerald green
      secondary: "#0891b2",     // Cyan
      background: "#f0fdf4",    // Light mint
      surface: "#ffffff",
      surfaceHover: "#ecfdf5",
      border: "#d1fae5",
      text: "#064e3b",
      textMuted: "#6b7280",
    },
    isDark: false,
  },
  whiteswan: {
    name: "WhiteSwan",
    fullName: "WhiteSwan Dental Clinic",
    industry: "Dental Healthcare",
    tagline: "Dental Intelligence Hub",
    logo: "/whiteswan-logo.png",
    theme: {
      primary: "#0EA5E9",       // Sky blue
      secondary: "#10B981",     // Mint green
      background: "#f8fafc",    // Light
      surface: "#ffffff",
      surfaceHover: "#f1f5f9",
      border: "#e2e8f0",
      text: "#0f172a",
      textMuted: "#64748b",
    },
    isDark: false,
  }
};

export function BrandProvider({ user, children }) {
  const [brand, setBrand] = useState('blackswan');
  const [brands, setBrands] = useState(['blackswan', 'whiteswan']);
  const [loading, setLoading] = useState(true);
  // userScope: { brand, region } if the logged-in user is restricted to a single (brand,region)
  const [userScope, setUserScope] = useState({ brand: null, region: null, role: null });

  useEffect(() => {
    // Fetch available brands from API once
    axios.get(`${API}/brands`)
      .then(res => {
        setBrands(res.data.brands || ['blackswan', 'whiteswan']);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  // Re-derive scope whenever the user prop changes (login / logout / refresh)
  useEffect(() => {
    if (user && user.role) {
      setUserScope({ brand: user.brand || null, region: user.region || null, role: user.role });
      if (user.brand && BRAND_CONFIG[user.brand]) {
        setBrand(user.brand);
        localStorage.setItem('selectedBrand', user.brand);
        return;
      }
    } else {
      setUserScope({ brand: null, region: null, role: null });
    }

    // Admin or no scope — load saved brand
    const savedBrand = localStorage.getItem('selectedBrand');
    if (savedBrand && BRAND_CONFIG[savedBrand]) {
      setBrand(savedBrand);
    }
  }, [user]);

  const switchBrand = (newBrand) => {
    // Block brand switching for brand-scoped users (only admins can switch freely)
    if (userScope.brand && userScope.role !== 'admin' && newBrand !== userScope.brand) {
      return;
    }
    if (BRAND_CONFIG[newBrand]) {
      setBrand(newBrand);
      localStorage.setItem('selectedBrand', newBrand);
    }
  };

  const currentConfig = BRAND_CONFIG[brand] || BRAND_CONFIG.blackswan;
  const canSwitchBrand = userScope.role === 'admin' || !userScope.brand;
  const canSwitchRegion = userScope.role === 'admin' || !userScope.region;

  return (
    <BrandContext.Provider value={{
      brand,
      brands,
      switchBrand,
      config: currentConfig,
      loading,
      userScope,
      canSwitchBrand,
      canSwitchRegion,
      lockedRegion: userScope.region,
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
